import { Flex, Box, VStack } from '@chakra-ui/react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { ActivityBar } from './ActivityBar';
import { statusFromPath } from '../routes';
import { AgentPanel } from '../components/AgentPanel';
import { isAgentPanelRoute } from '../components/AgentPanel/agentPanelRoute';
import { useAgentPanelViewport } from '../components/AgentPanel/useAgentPanelViewport';
import { isAgentShellV2Enabled } from '../lib/featureFlags';

// Layout chrome: 240px sidebar (left, sticky) + main content (max 1280px,
// centered, scrollable).
//
// The right-rail AgentPanel is a scaffold, not signed-off chrome. It
// mounts only when ALL of these hold:
//   - VITE_AGENT_SHELL_V2_ENABLED === 'true' (default off)
//   - the pathname is under /v2, or on the allowlist (empty by default)
//   - the viewport is at least 1280px
// Otherwise this component returns the pre-PR tree. /v2 routes exist
// only behind their per-route flags, so the shell flag alone never
// paints the panel on the live shell. When it does mount, it sits
// OUTSIDE <Outlet />.

export function PipelineShell() {
  const location = location_safe(useLocation());
  const stepStatuses = statusFromPath(location.pathname);
  const wideEnough = useAgentPanelViewport();
  const mountPanel = isAgentShellV2Enabled()
    && isAgentPanelRoute(location.pathname)
    && wideEnough;

  const originalLayout = (
    <Flex bg="brand.canvas" minH="100vh" align="stretch">
      <Sidebar stepStatuses={stepStatuses} />

      <Box flex={1} minW={0}>
        {/* Sticky activity bar — surfaces background pipeline state
            (DetectRun stages, brand enrichment) across every page in
            the shell. Self-hides when nothing's running. */}
        <ActivityBar />
        <Box maxW="1280px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
          <VStack align="stretch" spacing={8}>
            <Outlet />
          </VStack>
        </Box>
      </Box>
    </Flex>
  );

  if (!mountPanel) {
    return originalLayout;
  }

  // Scaffold only: wrap the original tree in a flex row with the
  // right rail. The main column keeps flex: 1. The panel is a sibling
  // of that column, outside the <Outlet /> subtree.
  return (
    <Flex bg="brand.canvas" minH="100vh" align="stretch">
      <Box flex="1" minW={0}>
        {originalLayout}
      </Box>
      <AgentPanel />
    </Flex>
  );
}

// useLocation can theoretically return null if used outside a Router,
// but inside the shell that never happens. Tiny helper to keep TS
// strict-mode happy without a non-null assertion sprinkled inline.
function location_safe(loc: ReturnType<typeof useLocation>) {
  return loc;
}
